import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import "../css/chapters.css";

// Chapters directory page
// - Lists all schools/chapters
// - Create New Chapter button
// - Click a school to navigate to edit-chapter
// This page is designed to be used standalone AND embedded inside the National Dashboard.

function getChapterName(ch) {
    return (
        ch.name ||
        ch.displayName ||
        ch.school ||
        ch.university ||
        ch.universityName ||
        ch.title ||
        "Untitled School"
    );
}

function getChapterCityState(ch) {
    const city = (ch.city || "").toString().trim();
    const state = (ch.state || "").toString().trim();
    if (city && state) return `${city}, ${state}`;
    return city || state || "";
}

function isActiveChapter(ch) {
    // Handle common schemas
    if (typeof ch.active === "boolean") return ch.active;
    if (typeof ch.isActive === "boolean") return ch.isActive;
    const status = (ch.status || "").toString().trim().toLowerCase();
    if (!status) return true; // default to active unless explicitly inactive
    return status !== "inactive" && status !== "archived";
}

function formatUpdatedAt(ch) {
    const ts = ch.updatedAt || ch.lastUpdated || ch.modifiedAt;
    if (ts && typeof ts.toDate === "function") {
        const d = ts.toDate();
        return d.toLocaleDateString();
    }
    if (ts instanceof Date) return ts.toLocaleDateString();
    return "";
}

function getSchoolsCollection(dbInst) {
    return collection(dbInst, "schools");
}

function getLegacySchoolCollection(dbInst) {
    return collection(dbInst, "school");
}

async function loadAllSchools(dbInst) {
    // Fetch from both collections and merge (some environments used `school` singular)
    // IMPORTANT: if Firestore rules allow `get` but not `list`, `getDocs()` will fail with
    // `permission-denied` and we'd previously return []. Instead, surface a helpful error.

    const tryFetch = async (ref, label) => {
        try {
            // IMPORTANT: do NOT rely on Firestore orderBy("name") here.
            // In some datasets, docs may not include a `name` field and the query can return 0 results.
            // Fetch the raw collection and sort client-side using `getChapterName`.
            const snap = await getDocs(ref);
            const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}), __source: label }));
            return { list, error: null };
        } catch (e) {
            console.warn(`Failed to read ${label} collection:`, e);
            const code = e?.code || "";
            const isPerm = code === "permission-denied";
            const msg = isPerm
                ? `Permission denied while listing ${label}. Firestore rules likely block collection reads (list).`
                : `Failed to read ${label} collection.`;
            return { list: [], error: { code, message: msg, raw: e } };
        }
    };

    const schoolsRef = getSchoolsCollection(dbInst);
    const legacyRef = getLegacySchoolCollection(dbInst);

    const [schoolsRes, legacyRes] = await Promise.all([
        tryFetch(schoolsRef, "schools"),
        tryFetch(legacyRef, "school"),
    ]);

    const schools = schoolsRes.list;
    const legacy = legacyRes.list;

    // Merge by id, prefer `schools` over legacy if same id exists
    const map = new Map();
    for (const ch of legacy) map.set(ch.id, ch);
    for (const ch of schools) map.set(ch.id, ch);

    const results = Array.from(map.values());
    results.sort((a, b) => getChapterName(a).localeCompare(getChapterName(b)));

    // Debug: if we got nothing, check if a known doc exists (helps detect env mismatch)
    if (results.length === 0) {
        let tamuExists = false;
        try {
            const test = await getDoc(doc(dbInst, "schools", "tamu"));
            tamuExists = test.exists();
            console.log("Chapters debug: schools/tamu exists?", tamuExists);
        } catch (e) {
            console.log("Chapters debug: could not read schools/tamu", e);
        }

        try {
            const test2 = await getDoc(doc(dbInst, "school", "tamu"));
            console.log("Chapters debug: school/tamu exists?", test2.exists());
        } catch (e) {
            console.log("Chapters debug: could not read school/tamu", e);
        }

        try {
            // eslint-disable-next-line no-console
            console.log("Chapters debug: firebase projectId:", dbInst.app?.options?.projectId);
        } catch {
            // ignore
        }

        // If tamu exists but lists are empty, it's almost certainly rules blocking list queries.
        const permError = schoolsRes.error?.code === "permission-denied" || legacyRes.error?.code === "permission-denied";
        if (tamuExists && permError) {
            const err = new Error(
                "Firestore rules are blocking collection reads (list) for chapters. `schools/tamu` is readable, but listing `schools` is denied. Update Firestore rules to allow list access for admins."
            );
            err.code = "permission-denied";
            throw err;
        }

        // If either fetch had an error, surface the most helpful one.
        const firstErr = schoolsRes.error || legacyRes.error;
        if (firstErr) {
            const err = new Error(firstErr.message || "Could not load chapters.");
            err.code = firstErr.code;
            throw err;
        }
    }

    return results;
}

export default function ChaptersPage({ embedded = false }) {
    const navigate = useNavigate();

    const [chapters, setChapters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [search, setSearch] = useState("");
    const [showInactive, setShowInactive] = useState(false);

    useEffect(() => {
        let mounted = true;

        async function load() {
            setLoading(true);
            setError("");

            try {
                const list = await loadAllSchools(db);
                if (!mounted) return;
                setChapters(list);
            } catch (e) {
                console.error("Failed to load chapters:", e);
                // Help debug common Firestore rules issue: allow `get` but not `list`
                if (e?.code === "permission-denied") {
                    console.warn("Chapters: Firestore rules likely block listing the `schools` collection. Allow list/read for admins.");
                }
                if (!mounted) return;
                setError(e?.message || "Could not load chapters.");
                setChapters([]);
            } finally {
                if (mounted) setLoading(false);
            }
        }

        load();
        return () => {
            mounted = false;
        };
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();

        return (chapters || [])
            .filter((ch) => {
                if (!showInactive && !isActiveChapter(ch)) return false;
                if (!q) return true;

                const name = getChapterName(ch).toLowerCase();
                const cityState = getChapterCityState(ch).toLowerCase();
                const campus = (ch.campus || ch.campusName || "").toString().toLowerCase();
                const contact = (ch.contactEmail || ch.email || ch.leaderEmail || "").toString().toLowerCase();
                const slug = (ch.slug || "").toString().toLowerCase();

                return (
                    name.includes(q) ||
                    cityState.includes(q) ||
                    campus.includes(q) ||
                    contact.includes(q) ||
                    slug.includes(q) ||
                    ch.id.toLowerCase().includes(q)
                );
            })
            .sort((a, b) => {
                // Sort active first, then alphabetically
                const aa = isActiveChapter(a) ? 0 : 1;
                const bb = isActiveChapter(b) ? 0 : 1;
                if (aa !== bb) return aa - bb;
                return getChapterName(a).localeCompare(getChapterName(b));
            });
    }, [chapters, search, showInactive]);

    const stats = useMemo(() => {
        const total = (chapters || []).length;
        const active = (chapters || []).filter((c) => isActiveChapter(c)).length;
        const inactive = total - active;
        return { total, active, inactive };
    }, [chapters]);

    const goCreate = () => {
        // Convention: /edit-chapter/new
        navigate("/edit-chapter/new");
    };

    const goEdit = (id) => {
        navigate(`/manage/${id}`);
    };

    return (
        <div className={`${embedded ? "nd-page" : "page"} chapters-page`}>
            <div
                className={`${embedded ? "nd-grid" : "grid"} chapters-grid`}
                style={{ display: "grid", gridTemplateColumns: "1fr", gap: "18px" }}
            >
                <div className={`${embedded ? "nd-panel" : "panel"} chapters-panel`}>
                    <div className={embedded ? "nd-panel-head" : "panel-head"}>
                        <div className={embedded ? "nd-panel-title" : "panel-title"}>Directory</div>
                        <div className={embedded ? "nd-panel-actions" : "panel-actions"}>
                            <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontWeight: 700 }}>
                                <input
                                    type="checkbox"
                                    checked={showInactive}
                                    onChange={(e) => setShowInactive(e.target.checked)}
                                />
                                Show inactive
                            </label>
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "12px" }}>
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by school, city, contact, or ID"
                            style={{
                                flex: 1,
                                minWidth: 0,
                                padding: "10px 12px",
                                borderRadius: "12px",
                                border: "1px solid rgba(15,23,42,0.14)",
                                boxSizing: "border-box",
                                fontWeight: 700,
                            }}
                        />

                        <div style={{ display: "flex", gap: "8px" }}>
                            <div
                                style={{
                                    padding: "10px 12px",
                                    borderRadius: "12px",
                                    border: "1px solid rgba(15,23,42,0.10)",
                                    background: "rgba(255,255,255,0.65)",
                                    fontWeight: 900,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {stats.active} active
                            </div>
                            <div
                                style={{
                                    padding: "10px 12px",
                                    borderRadius: "12px",
                                    border: "1px solid rgba(15,23,42,0.10)",
                                    background: "rgba(255,255,255,0.65)",
                                    fontWeight: 900,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {stats.total} total
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div style={{ opacity: 0.8, fontWeight: 700 }}>Loading…</div>
                    ) : error ? (
                        <div style={{ opacity: 0.9, fontWeight: 800 }}>{error}</div>
                    ) : filtered.length === 0 ? (
                        <div style={{ opacity: 0.8, fontWeight: 700 }}>No chapters found.</div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            {filtered.map((ch) => {
                                const name = getChapterName(ch);
                                const cityState = getChapterCityState(ch);
                                const active = isActiveChapter(ch);
                                const updated = formatUpdatedAt(ch);

                                return (
                                    <button
                                        key={ch.id}
                                        type="button"
                                        onClick={() => goEdit(ch.id)}
                                        style={{
                                            textAlign: "left",
                                            padding: "12px 14px",
                                            borderRadius: "14px",
                                            border: "1px solid rgba(15,23,42,0.10)",
                                            background: "linear-gradient(135deg, #ffffff, #f8fbff)",
                                            cursor: "pointer",
                                            display: "grid",
                                            gridTemplateColumns: "1fr auto",
                                            gap: "10px",
                                            alignItems: "center",
                                            boxSizing: "border-box",
                                        }}
                                    >
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                                                <div
                                                    style={{
                                                        fontSize: "1.0rem",
                                                        fontWeight: 900,
                                                        color: "#0f172a",
                                                        minWidth: 0,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {name}
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: "0.78rem",
                                                        fontWeight: 900,
                                                        padding: "4px 10px",
                                                        borderRadius: "999px",
                                                        border: "1px solid rgba(59,130,246,0.20)",
                                                        background: active ? "rgba(59,130,246,0.10)" : "rgba(148,163,184,0.15)",
                                                        color: active ? "#1c3650" : "#475569",
                                                        whiteSpace: "nowrap",
                                                        flex: "0 0 auto",
                                                    }}
                                                >
                                                    {active ? "Active" : "Inactive"}
                                                </div>
                                            </div>

                                            <div style={{ opacity: 0.75, fontWeight: 700, marginTop: "4px" }}>
                                                {cityState ? cityState : ""}
                                                {cityState && updated ? " • " : ""}
                                                {updated ? `Updated ${updated}` : ""}
                                            </div>

                                            {ch.groupme || ch.groupMe || ch.groupmeLink ? (
                                                <div style={{ marginTop: "6px", opacity: 0.75, fontWeight: 700 }}>
                                                    GroupMe linked
                                                </div>
                                            ) : null}
                                        </div>

                                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                            <div
                                                style={{
                                                    fontSize: "0.85rem",
                                                    fontWeight: 900,
                                                    opacity: 0.8,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                Edit
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className={`${embedded ? "nd-panel" : "panel"} chapters-panel`}>
                    <div className={embedded ? "nd-panel-head" : "panel-head"}>
                        <div className={embedded ? "nd-panel-title" : "panel-title"}>Quick actions</div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <button className={embedded ? "nd-btn" : "btn"} type="button" onClick={goCreate}>
                            Create a new chapter
                        </button>

                        <Link className={embedded ? "nd-btn" : "btn"} to="/users" style={{ textDecoration: "none", textAlign: "center" }}>
                            Manage national members
                        </Link>

                        <div
                            style={{
                                padding: "12px 14px",
                                borderRadius: "14px",
                                border: "1px dashed rgba(15,23,42,0.18)",
                                background: "rgba(255,255,255,0.55)",
                                fontWeight: 700,
                                opacity: 0.85,
                                lineHeight: 1.35,
                            }}
                        >
                            Suggested fields per chapter: leaders, contact email, GroupMe link, meeting location/time, curriculum version, and last updated.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Optional: compact list widget for embedding into dashboards
export function ChaptersWidget({ title = "Chapters", maxItems = 8 }) {
    const navigate = useNavigate();
    const [chapters, setChapters] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;

        async function load() {
            try {
                setLoading(true);
                const list = await loadAllSchools(db);
                list.sort((a, b) => getChapterName(a).localeCompare(getChapterName(b)));
                if (mounted) setChapters(list);
            } catch {
                if (mounted) setChapters([]);
            } finally {
                if (mounted) setLoading(false);
            }
        }

        load();
        return () => {
            mounted = false;
        };
    }, []);

    const shown = useMemo(() => chapters.slice(0, Math.max(0, maxItems)), [chapters, maxItems]);

    return (
        <div className="nd-placeholder">
            <div className="nd-placeholder-title">{title}</div>
            {loading ? (
                <div className="nd-placeholder-text">Loading…</div>
            ) : shown.length === 0 ? (
                <div className="nd-placeholder-text">No chapters found.</div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "10px" }}>
                    {shown.map((ch) => (
                        <button
                            key={ch.id}
                            type="button"
                            className="nd-btn"
                            style={{ justifyContent: "space-between" }}
                            onClick={() => navigate(`/edit-chapter/${ch.id}`)}
                        >
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{getChapterName(ch)}</span>
                            <span style={{ opacity: 0.8 }}>Edit</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
